import client from 'braintree-web/client';
import googlePayment from 'braintree-web/google-payment';
import dataCollector from 'braintree-web/data-collector';
import {
  notifyApmAvailability,
  createButtonStateObserver,
  getCountryName,
} from './apm-utils';
import { t } from '../utils/i18n';

// Load Google Pay script dynamically
const loadGooglePayScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google?.payments?.api?.PaymentsClient) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://pay.google.com/gp/p/js/pay.js';
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const initGooglePay = async function () {
  const googlePayConfigElement = document.getElementById('google-pay-config');
  if (!googlePayConfigElement) {
    notifyApmAvailability('googlePay', false);
    return;
  }

  const googlePayConfig = JSON.parse(googlePayConfigElement.innerHTML);
  const totalAmount = googlePayConfig.totalAmount;
  const clientTokenUrl = googlePayConfig.clientTokenUrl;

  const googlePayButton = document.getElementById('google-pay-button');
  const form = document.getElementById('google-pay-form');
  const paymentMethodNonceField = document.getElementById(
    'google_pay_payment_method_nonce',
  );
  const deviceDataField = document.getElementById('google_pay_device_data');
  const billingAddressField = document.getElementById(
    'google_pay_billing_address',
  );

  if (!googlePayButton || !form) {
    return;
  }

  // Load Google Pay script
  try {
    await loadGooglePayScript();
  } catch (error) {
    console.error('Error loading Google Pay script:', error);
    return;
  }

  // Initialize Google Pay client
  const paymentsClient = new google.payments.api.PaymentsClient({
    environment: googlePayConfig.environment || 'TEST',
  });

  createButtonStateObserver(googlePayButton);
  // Fetch client token only when Google Pay is available
  let clientToken;
  try {
    const response = await fetch(clientTokenUrl);
    const data = await response.json();
    clientToken = data.clientToken;
  } catch (error) {
    console.error('Error fetching client token:', error);
    return;
  }

  client.create(
    {
      authorization: clientToken,
    },
    function (clientErr, clientInstance) {
      if (clientErr) {
        console.error('Error creating Braintree client:', clientErr);
        return;
      }

      let deviceData = '';
      dataCollector.create(
        {
          client: clientInstance,
        },
        function (dataCollectorErr, dataCollectorInstance) {
          if (dataCollectorErr) {
            console.error('Error creating data collector:', dataCollectorErr);
            return;
          }

          deviceData = dataCollectorInstance.deviceData;
        },
      );

      const googlePaymentConfig = {
        client: clientInstance,
        googlePayVersion: 2,
      };

      if (
        googlePayConfig.googleMerchantId &&
        googlePayConfig.googleMerchantId.trim() !== ''
      ) {
        googlePaymentConfig.googleMerchantId = googlePayConfig.googleMerchantId;
      }

      googlePayment.create(
        googlePaymentConfig,
        function (googlePaymentErr, googlePaymentInstance) {
          if (googlePaymentErr) {
            console.error(
              'Error creating Google Payment instance:',
              googlePaymentErr,
            );
            return;
          }

          // Check if Google Pay is ready
          paymentsClient
            .isReadyToPay({
              apiVersion: 2,
              apiVersionMinor: 0,
              allowedPaymentMethods:
                googlePaymentInstance.createPaymentDataRequest()
                  .allowedPaymentMethods,
              existingPaymentMethodRequired: true,
            })
            .then(function (response) {
              if (!response.result) {
                console.log('Google Pay is not available');
                notifyApmAvailability('googlePay', false);
                return;
              }

              // Show the Google Pay button
              form.style.display = 'inline-block';

              const skeletons = document.getElementById(
                'payment-button-skeletons',
              );
              if (skeletons) {
                skeletons.style.display = 'none';
              }

              notifyApmAvailability('googlePay', true);

              googlePayButton.addEventListener('keydown', function (event) {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  googlePayButton.click();
                }
              });

              googlePayButton.addEventListener('click', function (event) {
                event.preventDefault();

                const termsCheckbox = document.getElementById(
                  'cart-terms-checkbox',
                );
                if (termsCheckbox && !termsCheckbox.checked) {
                  alert(t('js.billing.accept_terms'));
                  return;
                }

                if (googlePayButton.hasAttribute('disabled')) {
                  return;
                }

                googlePayButton.style.opacity = '0.6';
                googlePayButton.style.pointerEvents = 'none';

                const paymentDataRequest =
                  googlePaymentInstance.createPaymentDataRequest({
                    transactionInfo: {
                      currencyCode: 'USD',
                      totalPriceStatus: 'FINAL',
                      totalPrice: totalAmount,
                    },
                  });

                // Add billing address requirements
                const cardPaymentMethod =
                  paymentDataRequest.allowedPaymentMethods[0];
                cardPaymentMethod.parameters.billingAddressRequired = true;
                cardPaymentMethod.parameters.billingAddressParameters = {
                  format: 'FULL',
                  phoneNumberRequired: true,
                };

                // Add email and phone requirements
                paymentDataRequest.emailRequired = true;
                paymentDataRequest.shippingAddressRequired = false;

                paymentsClient
                  .loadPaymentData(paymentDataRequest)
                  .then(function (paymentData) {
                    googlePaymentInstance.parseResponse(
                      paymentData,
                      function (parseErr, result) {
                        if (parseErr) {
                          console.error(
                            'Error parsing Google Pay response:',
                            parseErr,
                          );
                          resetButton();
                          return;
                        }

                        // Extract billing address from paymentData
                        const billingAddress =
                          paymentData.paymentMethodData.info.billingAddress;
                        const countryCode = billingAddress.countryCode || 'US';
                        const countryName = getCountryName(countryCode, 'US');

                        const addressLines = [
                          billingAddress.address1 || '',
                          billingAddress.address2 || '',
                          billingAddress.address3 || '',
                        ].filter((line) => line.length > 0);

                        const billingAddressData = {
                          givenName: billingAddress.name
                            ? billingAddress.name.split(' ')[0]
                            : '',
                          familyName: billingAddress.name
                            ? billingAddress.name.split(' ').slice(1).join(' ')
                            : '',
                          phoneNumber: billingAddress.phoneNumber || '',
                          emailAddress: paymentData.email || '',
                          addressLines: addressLines,
                          locality: billingAddress.locality || '',
                          administrativeArea:
                            billingAddress.administrativeArea || '',
                          postalCode: billingAddress.postalCode || '',
                          countryCode: countryCode,
                          countryName: countryName,
                        };

                        paymentMethodNonceField.value = result.nonce;
                        deviceDataField.value = deviceData;
                        billingAddressField.value =
                          JSON.stringify(billingAddressData);

                        form.submit();
                      },
                    );
                  })
                  .catch(function (error) {
                    if (error.statusCode === 'CANCELED') {
                      console.log('User canceled Google Pay');
                    } else {
                      console.error('Error loading payment data:', error);
                    }
                    resetButton();
                  });
              });

              function resetButton() {
                googlePayButton.style.opacity = '1';
                googlePayButton.style.pointerEvents = 'auto';
              }
            })
            .catch(function (err) {
              console.error('Error checking if ready to pay:', err);
            });
        },
      );
    },
  );
};

document.addEventListener('readystatechange', function () {
  if (document.readyState === 'complete') {
    initGooglePay();
  }
});

export default initGooglePay;
